<script setup lang="ts">
import { computed, ref } from "vue";
import PaperclipIcon from "@/components/shared/icons/PaperclipIcon.vue";
import type { AttachButtonProps } from "../types";

const props = withDefaults(defineProps<AttachButtonProps>(), {
  disabled: false,
});

const emit = defineEmits<(e: "select", files: File[]) => void>();

const fileInputRef = ref<HTMLInputElement | undefined>();

// Disabled buttons don't emit hover events, so the tooltip lives on the wrapper.
const tooltipConfig = computed(() => ({
  value: "Only one file can be attached. Remove the current file to replace it.",
  disabled: !props.disabled,
  class: "bg-white max-w-xs shaddow rounded white",
  pt: {
    text: "text-indigo-base p-3 leading-none",
  },
}));

const openFilePicker = (): void => {
  fileInputRef.value?.click();
};

const onFileChange = (event: Event): void => {
  const input = event.target as HTMLInputElement;
  emit("select", Array.from(input.files ?? []));
  input.value = "";
};
</script>

<template>
  <span v-tooltip.top="tooltipConfig" class="inline-flex shrink-0">
    <button
      type="button"
      class="inline-flex items-center gap-1.5 h-[34px] px-3 rounded-md border border-gray-475 bg-white text-12.5p font-semibold text-indigo-base hover:bg-gray-125 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-125"
      :disabled="disabled"
      data-testid="internal-comment-attach-button"
      @click="openFilePicker"
    >
      <PaperclipIcon class="w-4 h-4" />
      Attach file
    </button>
    <input
      ref="fileInputRef"
      type="file"
      class="hidden"
      data-testid="internal-comment-file-input"
      @change="onFileChange"
    />
  </span>
</template>
